/**
 * MovimientosKioskView -- REGISTRAR LO QUE PASO CON UN INSUMO, DESDE EL KIOSCO.
 *
 * TAREA: [AL-T3] habilitar movimientos de inventario en el kiosk.
 *
 * AUDIENCIA: EL COLABORADOR del negocio, en la TABLET del kiosco. No es el
 * dueno y no es escritorio. Es alguien de pie, con las manos ocupadas, que
 * acaba de botar algo o de porcionar un insumo y tiene que anotarlo en veinte
 * segundos. Por eso: botones grandes, pocos pasos, y ni una palabra de
 * contabilidad.
 *
 * REGLA E (buscado antes de crear, bitacora #9316): NO se creo cliente ni
 * sistema de identificacion. Ya existian los dos y se reusan:
 *   - ComprasApiClient.createMovimiento() -> POST /movimientos-inventario
 *   - OperadorPin (getOperador / setupOperadorPin) -> quien esta registrando
 *
 * EL VOCABULARIO ES CANON, no invento (MOV-VOCABULARIO-DOMINIO-V1-001, Josse
 * 21-jul, tal como lo documenta el DTO de la API):
 *   DESPERDICIO = baja aprobada de items (reemplaza a MERMA)
 *   DANO        = se dano
 *   DEVOLUCION  = se devolvio al proveedor
 *   AUMENTO     = variacion positiva del insumo AL PROCESARSE. NO es ENTRADA.
 *                 Este es el "porcionado" que pide la tarea.
 * ENTRADA no se ofrece a proposito: el controller la reserva al flujo de
 * compras por OCR. El colaborador anota lo que sale o se transforma, no
 * declara que entro mercancia.
 *
 * ANTI-DUPLICADO: la tabla tiene UNIQUE (business_id, insumo_id, tipo,
 * referencia). Si dos personas registran la misma merma del mismo insumo el
 * mismo dia sin referencia distinta, la segunda choca. Por eso la referencia
 * se genera con hora y operador.
 */

import { ComprasApiClient } from '../../infrastructure/api/ComprasApiClient.js';
import { InventarioApiClient } from '../../infrastructure/api/InventarioApiClient.js';
import { getOperador, setupOperadorPin } from '../components/OperadorPin.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';
import { parsearNumeroColombiano } from '../shared/numeroColombiano.js';

const C = {
  ok: 'var(--accent-green-text)', ojo: 'var(--accent-yellow)', mal: 'var(--accent-red)', frio: 'var(--accent-blue)',
  texto: 'var(--text-primary)', suave: 'var(--text-secondary)', tenue: 'var(--text-muted)',
  panel: 'var(--bg-secondary)', fondo: 'var(--bg-canvas)', borde: 'var(--border-default)'
};

/** Los 4 movimientos que el colaborador puede registrar, en su idioma. */
const ACCIONES = [
  { tipo: 'DESPERDICIO', label: 'Se boto', icono: '&#128465;', color: 'var(--accent-yellow)',
    ayuda: 'Se vencio, se cayo o ya no servia' },
  { tipo: 'DANO', label: 'Se dano', icono: '&#128165;', color: 'var(--accent-red)',
    ayuda: 'Llego malo o se dano guardado' },
  { tipo: 'DEVOLUCION', label: 'Se devolvio', icono: '&#8617;&#65039;', color: 'var(--accent-blue)',
    ayuda: 'Se le regreso al proveedor' },
  { tipo: 'AUMENTO', label: 'Se porciono', icono: '&#127860;', color: 'var(--accent-green-text)',
    ayuda: 'Se preparo o se corto en porciones' }
];

// Deuda 4: este escape (no cambiaba la comilla simple) es ahora el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';

/** Referencia unica: la tabla la usa como anti-duplicado, asi que no puede repetirse. */
function referenciaDe(accion, operador) {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  const sello = d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate())
    + ' ' + p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds());
  const quien = (operador && operador.nombre) ? operador.nombre : 'kiosco';
  return accion.label + ' ' + sello + ' - ' + quien;
}

const estado = { insumos: [], insumo: null, accion: null, cantidad: '', enviando: false, msg: null };

export async function renderMovimientosKiosk(container) {
  // El estado vive en el modulo, asi que SOBREVIVE a salir y volver a entrar.
  // Sin este reset, el colaborador que vuelve se encuentra el "Quedo anotado"
  // del movimiento anterior en vez del formulario, y puede creer que ya anoto
  // algo que todavia no anoto. Lo caza el smoke, no la lectura.
  estado.insumos = [];
  estado.insumo = null;
  estado.accion = null;
  estado.cantidad = '';
  estado.enviando = false;
  estado.msg = null;

  const $el = container;
  const bizId = getSelectedBusiness();

  $el.innerHTML = '<div class="detail-section">'
    + regresoHtml({ texto: 'Inicio' })
    + '<h1 style="margin:6px 0 0;font-size:1.1rem;color:' + C.texto + '">&#128230; Anotar un movimiento</h1>'
    + '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:4px">'
    + 'Se boto algo, se dano, se devolvio o se porciono? Anotalo aca.</p>'
    + '<div data-mk-operador style="margin-top:10px"></div>'
    + '</div>'
    + '<div data-mk-cuerpo style="margin-top:14px"></div>';

  // Gate: primero saber QUIEN esta anotando. Se reusa OperadorPin.
  const $op = $el.querySelector('[data-mk-operador]');
  const $cuerpo = $el.querySelector('[data-mk-cuerpo]');
  $op.innerHTML = '<div id="nx-operador-pin"></div>';
  try { await setupOperadorPin(); } catch (e) { /* el gate se dibuja igual abajo */ }

  if (!bizId) {
    $cuerpo.innerHTML = aviso('Elegi un negocio', 'Arriba, en el selector, para poder anotar movimientos.', C.ojo);
    return;
  }

  // Cargar insumos una vez (el cliente ya pagina y cachea).
  $cuerpo.innerHTML = '<div style="padding:26px;text-align:center;color:' + C.suave + '">Cargando insumos...</div>';
  try {
    const res = await InventarioApiClient.getInsumosWithConfig(bizId);
    if (res && res.ok) {
      estado.insumos = (res.data || res.items || []).map((i) => ({
        id: i.id || i.Id,
        nombre: i.nombre || i.Nombre || '(sin nombre)',
        grupo: i.Grupo || i.grupo || ''
      })).filter((i) => i.id && i.nombre);
    } else {
      $cuerpo.innerHTML = aviso('No pudimos traer los insumos',
        'La lista no respondio. Reintenta en un momento.', C.mal);
      return;
    }
  } catch (e) {
    $cuerpo.innerHTML = aviso('No pudimos traer los insumos', 'Reintenta en un momento.', C.mal);
    return;
  }

  pintar($cuerpo, bizId);
}

function aviso(titulo, cuerpo, color) {
  return '<div style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-left:3px solid ' + color
    + ';border-radius:10px;padding:18px;text-align:center">'
    + '<div style="font-size:.92rem;color:' + C.texto + ';font-weight:700">' + esc(titulo) + '</div>'
    + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:8px">' + esc(cuerpo) + '</div></div>';
}

function pintar($cuerpo, bizId) {
  const op = getOperador();

  if (estado.msg) {
    const bueno = estado.msg.tipo === 'ok';
    $cuerpo.innerHTML = '<div style="background:' + C.panel + ';border:1px solid ' + C.borde
      + ';border-left:3px solid ' + (bueno ? C.ok : C.mal) + ';border-radius:10px;padding:22px;text-align:center">'
      + '<div style="font-size:2rem">' + (bueno ? '&#9989;' : '&#9888;&#65039;') + '</div>'
      + '<div style="font-size:.95rem;color:' + C.texto + ';font-weight:700;margin-top:8px">' + esc(estado.msg.titulo) + '</div>'
      + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:8px;line-height:1.6">' + esc(estado.msg.cuerpo) + '</div>'
      + '<button data-mk-otro style="margin-top:16px;padding:12px 22px;font-size:.88rem;background:'
      + C.frio + ';color:var(--text-on-accent);border:0;border-radius:8px;cursor:pointer;font-weight:700">Anotar otro</button>'
      + '</div>';
    const $otro = $cuerpo.querySelector('[data-mk-otro]');
    if ($otro) $otro.addEventListener('click', () => {
      estado.msg = null; estado.insumo = null; estado.accion = null; estado.cantidad = '';
      pintar($cuerpo, bizId);
    });
    return;
  }

  // Paso 1: que insumo
  const listaFiltrada = estado.insumos.slice(0, 60);
  const paso1 = '<div style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-radius:10px;padding:14px">'
    + '<div style="font-size:0.875rem;color:' + C.texto + ';font-weight:700;margin-bottom:8px">1. Cual insumo?</div>'
    + '<input data-mk-buscar type="text" placeholder="Escribi el nombre..." '
    + 'style="width:100%;padding:13px;font-size:1rem;background:' + C.fondo + ';border:1px solid ' + C.borde
    + ';border-radius:8px;color:' + C.texto + '" value="">'
    + '<div data-mk-lista style="margin-top:8px;max-height:210px;overflow-y:auto">'
    + listaFiltrada.map(itemInsumo).join('') + '</div>'
    + (estado.insumo ? '<div style="margin-top:10px;padding:10px;background:' + C.fondo + ';border:1px solid '
      + C.ok + '55;border-radius:8px;font-size:.88rem;color:' + C.ok + ';font-weight:700">&#10003; '
      + esc(estado.insumo.nombre) + '</div>' : '')
    + '</div>';

  // Paso 2: que paso
  const paso2 = '<div style="background:' + C.panel + ';border:1px solid ' + C.borde
    + ';border-radius:10px;padding:14px;margin-top:12px">'
    + '<div style="font-size:0.875rem;color:' + C.texto + ';font-weight:700;margin-bottom:10px">2. Que paso?</div>'
    + '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px">'
    + ACCIONES.map((a) => {
      const sel = estado.accion && estado.accion.tipo === a.tipo;
      return '<button data-mk-accion="' + a.tipo + '" style="padding:16px 10px;border-radius:10px;cursor:pointer;'
        + 'background:' + (sel ? a.color + '22' : C.fondo) + ';border:2px solid ' + (sel ? a.color : C.borde) + ';'
        + 'display:flex;flex-direction:column;align-items:center;gap:6px">'
        + '<span style="font-size:1.5rem">' + a.icono + '</span>'
        + '<span style="font-size:.9rem;color:' + (sel ? a.color : C.texto) + ';font-weight:700">' + a.label + '</span>'
        + '<span style="font-size:0.875rem;color:' + C.tenue + ';text-align:center;line-height:1.3">' + a.ayuda + '</span>'
        + '</button>';
    }).join('') + '</div></div>';

  // Paso 3: cuanto
  const paso3 = '<div style="background:' + C.panel + ';border:1px solid ' + C.borde
    + ';border-radius:10px;padding:14px;margin-top:12px">'
    + '<div style="font-size:0.875rem;color:' + C.texto + ';font-weight:700;margin-bottom:8px">3. Cuanto?</div>'
    + '<input data-mk-cantidad type="text" inputmode="decimal" placeholder="0" value="' + esc(estado.cantidad) + '" '
    + 'style="width:100%;padding:15px;font-size:1.5rem;text-align:center;font-weight:700;background:' + C.fondo
    + ';border:1px solid ' + C.borde + ';border-radius:8px;color:' + C.texto + '">'
    + '</div>';

  const listo = estado.insumo && estado.accion && parsearNumeroColombiano(estado.cantidad) > 0;
  const quien = op && op.nombre
    ? '<div style="font-size:0.875rem;color:' + C.suave + ';text-align:center;margin-top:8px">Queda anotado a nombre de <strong style="color:' + C.texto + '">' + esc(op.nombre) + '</strong></div>'
    : '<div style="font-size:0.875rem;color:' + C.ojo + ';text-align:center;margin-top:8px">Identificate arriba con tu PIN para que quede a tu nombre</div>';

  const boton = '<button data-mk-guardar ' + (listo && !estado.enviando ? '' : 'disabled')
    + ' style="width:100%;margin-top:14px;padding:17px;font-size:1.05rem;font-weight:700;border:0;border-radius:10px;'
    + 'cursor:' + (listo ? 'pointer' : 'not-allowed') + ';background:' + (listo ? C.ok : C.borde) + ';color:'
    + (listo ? 'var(--text-on-accent)' : C.tenue) + '">' + (estado.enviando ? 'Guardando...' : 'Anotar') + '</button>';

  $cuerpo.innerHTML = paso1 + paso2 + paso3 + boton + quien;
  enlazar($cuerpo, bizId);
}

function itemInsumo(i) {
  return '<div data-mk-insumo="' + esc(i.id) + '" style="padding:11px 12px;border-bottom:1px solid ' + C.borde
    + ';cursor:pointer;font-size:0.875rem;color:' + C.texto + '">' + esc(i.nombre)
    + (i.grupo ? '<span style="font-size:0.875rem;color:' + C.tenue + ';margin-left:8px">' + esc(i.grupo) + '</span>' : '')
    + '</div>';
}

function enlazar($cuerpo, bizId) {
  const $buscar = $cuerpo.querySelector('[data-mk-buscar]');
  const $lista = $cuerpo.querySelector('[data-mk-lista]');
  const $cant = $cuerpo.querySelector('[data-mk-cantidad]');

  // El buscador NO se repinta al teclear: se repinta la LISTA. Si no, se pierde
  // el foco y el cursor a cada letra (trampa conocida del panel).
  if ($buscar && $lista) {
    $buscar.addEventListener('input', () => {
      const q = $buscar.value.trim().toLowerCase();
      const f = (q ? estado.insumos.filter((i) => i.nombre.toLowerCase().includes(q)) : estado.insumos).slice(0, 60);
      $lista.innerHTML = f.length ? f.map(itemInsumo).join('')
        : '<div style="padding:12px;color:' + C.tenue + ';font-size:0.875rem">Ningun insumo con ese nombre.</div>';
    });
  }

  $cuerpo.querySelectorAll('[data-mk-insumo]').forEach((el) => {
    el.addEventListener('click', () => {
      const id = el.getAttribute('data-mk-insumo');
      estado.insumo = estado.insumos.find((i) => String(i.id) === String(id)) || null;
      pintar($cuerpo, bizId);
    });
  });

  $cuerpo.querySelectorAll('[data-mk-accion]').forEach((el) => {
    el.addEventListener('click', () => {
      const t = el.getAttribute('data-mk-accion');
      estado.accion = ACCIONES.find((a) => a.tipo === t) || null;
      pintar($cuerpo, bizId);
    });
  });

  if ($cant) {
    $cant.addEventListener('input', () => {
      estado.cantidad = $cant.value;
      const $btn = $cuerpo.querySelector('[data-mk-guardar]');
      const listo = estado.insumo && estado.accion && parsearNumeroColombiano(estado.cantidad) > 0;
      if ($btn) {
        $btn.disabled = !listo || estado.enviando;
        $btn.style.background = listo ? C.ok : C.borde;
        $btn.style.color = listo ? 'var(--text-on-accent)' : C.tenue;
        $btn.style.cursor = listo ? 'pointer' : 'not-allowed';
      }
    });
  }

  const $guardar = $cuerpo.querySelector('[data-mk-guardar]');
  if ($guardar) $guardar.addEventListener('click', () => guardar($cuerpo, bizId));
}

async function guardar($cuerpo, bizId) {
  if (estado.enviando) return;                       // candado: doble toque no manda dos veces
  const cantidad = parsearNumeroColombiano(estado.cantidad);
  if (!estado.insumo || !estado.accion || !(cantidad > 0)) return;

  estado.enviando = true;
  const $btn = $cuerpo.querySelector('[data-mk-guardar]');
  if ($btn) { $btn.disabled = true; $btn.textContent = 'Guardando...'; }

  const op = getOperador();
  try {
    const res = await ComprasApiClient.createMovimiento(bizId, {
      insumoId: estado.insumo.id,
      tipo: estado.accion.tipo,
      cantidad,
      usuario: (op && op.nombre) ? op.nombre : 'kiosco',
      referencia: referenciaDe(estado.accion, op),
      colaboradorUuid: (op && op.id) ? op.id : null
    });

    if (res && res.ok) {
      estado.msg = {
        tipo: 'ok',
        titulo: 'Quedo anotado',
        cuerpo: estado.accion.label + ' ' + cantidad + ' de ' + estado.insumo.nombre + '.'
      };
    } else {
      const err = res && res.error;
      const txt = JSON.stringify(err || '');
      let cuerpo = 'No se pudo guardar. Reintenta en un momento.';
      if (/409|duplicad|unique/i.test(txt)) {
        cuerpo = 'Parece que este mismo movimiento ya se anoto hace un instante. Revisa antes de repetirlo.';
      } else if (/401|403/i.test(txt)) {
        cuerpo = 'La sesion del kiosco no tiene permiso para anotar movimientos.';
      } else if (/404/i.test(txt)) {
        cuerpo = 'El insumo ya no existe en el catalogo.';
      } else if (err && (err.message || typeof err === 'string')) {
        cuerpo = 'No se pudo guardar: ' + (err.message || err);
      }
      estado.msg = { tipo: 'error', titulo: 'No quedo anotado', cuerpo };
    }
  } catch (e) {
    estado.msg = { tipo: 'error', titulo: 'No quedo anotado', cuerpo: 'No pudimos hablar con el servidor. Reintenta.' };
  }

  estado.enviando = false;
  pintar($cuerpo, bizId);
}
